import * as React from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import DetailDrawer from "@/components/DetailDrawer";
import EntityAvatar from "@/components/identity/EntityAvatar";

/**
 * Search drawer — supports `from:name`, `in:slug`, `has:file`,
 * `before:YYYY-MM-DD`, `after:YYYY-MM-DD`, `on:YYYY-MM-DD` filters
 * followed by free-text.
 */
export default function SearchDrawer({ open, onOpenChange, companyId, onJump }) {
  const [q, setQ] = React.useState("");
  const [results, setResults] = React.useState([]);
  const [filters, setFilters] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  const [note, setNote] = React.useState("");

  React.useEffect(() => { if (open) { setQ(""); setResults([]); setFilters({}); setNote(""); } }, [open]);

  async function run() {
    if (!q.trim()) return;
    setBusy(true); setNote("");
    try {
      const { data } = await api.get(`/companies/${companyId}/comms/search`, {
        params: { q: q.trim(), limit: 50 },
      });
      setResults(data.messages || []);
      setFilters(data.filters || {});
      if (data.note) setNote(data.note);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Search failed");
    } finally { setBusy(false); }
  }

  return (
    <DetailDrawer
      open={open}
      onOpenChange={onOpenChange}
      title="Search comms"
      subtitle="from: · in: · has:file · before: · after: · on:"
      testid="comms-search-drawer"
    >
      <div className="px-5 pt-4 pb-3 border-b border-brand-rule">
        <div className="flex gap-2">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run()}
            placeholder='e.g. from:"Isla Mackenzie" in:site-dockside overtime'
            className="field-input h-9 flex-1 text-[13px]"
            data-testid="comms-search-input"
            autoFocus
          />
          <button
            type="button"
            onClick={run}
            disabled={busy || !q.trim()}
            className="btn-violet h-9"
            data-testid="comms-search-run"
          >
            <Search size={13} /> {busy ? "…" : "Search"}
          </button>
        </div>
        {Object.keys(filters).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {Object.entries(filters).map(([k, v]) => (
              <span key={k} className="chip mono-num text-[10.5px]">
                {k}:{v}
              </span>
            ))}
          </div>
        )}
      </div>
      {note && (
        <div className="p-4 text-[12.5px] text-brand-muted" data-testid="comms-search-note">{note}</div>
      )}
      {results.length > 0 ? (
        <div className="p-4 space-y-3" data-testid="comms-search-results">
          {results.map((m) => (
            <div
              key={m.id}
              className="card-panel p-3 hover:bg-brand-hush cursor-pointer"
              onClick={() => { onJump && onJump(m.conversation_id); onOpenChange(false); }}
              data-testid={`comms-search-result-${m.id}`}
            >
              <div className="flex items-center gap-2">
                <EntityAvatar kind="person" name={m.sender_name || "?"} id={m.sender_id || m.sender_name} size={22} />
                <div className="text-[12.5px] font-semibold text-brand-ink truncate">{m.sender_name}</div>
                <div className="mono-num text-[10.5px] text-brand-muted">
                  {new Date(m.created_at).toLocaleString()}
                </div>
              </div>
              <div className="text-[12.5px] text-brand-ink whitespace-pre-wrap break-words mt-1">
                {(m.body || "").slice(0, 320)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        !busy && !note && q && (
          <div className="p-6 text-[12.5px] text-brand-muted" data-testid="comms-search-empty">
            No matches. Try relaxing your filters.
          </div>
        )
      )}
    </DetailDrawer>
  );
}
